/* eslint-disable node/no-callback-literal */
import { AUTH } from 'constants/api'
import { useContext, useState } from 'react'
import { AuthError, GoogleAuthProvider, getAuth, OAuthCredential, signInWithPopup } from '@firebase/auth'
import axios, { AxiosError } from 'axios'
import utmServices from 'services/utm-services'
import ProfileService from 'services/profile-services'
import { UserData } from 'contexts/session-context/session-reducer'
import GoogleIcon from 'components/icon/google'
import { dlSetUserInfo, dlTrackEvent, createUserProperties } from 'components/dataLayer/dataLayer'
import useRedirect from 'hooks/use-redirect'
import useLocalStorage from 'hooks/use-local-storage'
import { isInAppBrowser } from 'utilities/detectDeviceAgent'
import { getBirthdayLocal } from 'utilities/birthday'
import MetaCAPIService from 'services/meta-capi.services'
import { Prize } from 'views/prizes/types'
import useRouter from 'hooks/use-router'
import textServices from 'services/text-services'
import CONFIGS from 'build/Config'
import AlertAppView from '../alertAppView'
import { LoaderContext } from '../../../contexts/loader-context'
import { goToUrlGoogle } from './goToUrlGoogle'

function Google({ setSession, setUser, setLoginError, setError, setFirebaseError, setAllowCookies, setPrize, isHorizontal }) {
  const { setLoad } = useContext(LoaderContext)
  const [{ url }, { redirect }] = useRedirect()
  const auth = getAuth()
  const provider = new GoogleAuthProvider()
  const [flowType] = useLocalStorage('flowType')
  const [alertAppView, setAlertAppView] = useState(false);
  const [birthday] = useState(getBirthdayLocal())
  const { push } = useRouter();

  const completeData = (user: UserData, session: Object) => {
    setSession(session)
    setUser(user)

    const external = localStorage.getItem('toexternal')
    if (external) {
      localStorage.removeItem('toexternal')
      return window.location.assign(decodeURI(JSON.parse(external)))
    };


    if (url) return redirect();

    const availableFields = CONFIGS?.notAvailableFields
    const isComplete = ((availableFields?.includes('nickName') ? false : !user?.nickName) || !user?.birthday || (!user?.genre || !user?.genreId))
    if (!CONFIGS?.signUp?.base && isComplete) return push('/information/update?isNew=true');

    return push('/home')
  }

  const callback = async (data?: {
    termsOfUse?: boolean
    privacyPolicy?: boolean
    subscribe?: boolean
    code?: string
    session: Object
    user: UserData
    prize?: Prize
  }) => {
    setLoad(true)
    const { session, user, termsOfUse, privacyPolicy, subscribe, code, prize } = data || {}
    try {
      dlSetUserInfo(user?.hashId ?? user?.id)
      createUserProperties(user, 'google')
      let payload = { termsOfUse, privacyPolicy, subscribe, code }
      const consumption = localStorage.getItem('consumption')

      if (consumption) {
        payload = { ...payload, consumptionCenterId: JSON.parse(consumption) ?? 0 } as any
      }

      let userData = user
      if (termsOfUse || privacyPolicy || subscribe || code) {
        const response = await ProfileService.socialLogin({
          payload: payload,
          credentials: session
        })

        userData = response?.data
      }

      if (prize) return setPrize({ prize, name: userData?.firstName, callback: () => completeData(userData, session) })

      return completeData(userData, session)
    } catch (error) {
      setAllowCookies({ callback, isAllow: false })
      const code = (error as AxiosError).response?.data.code || 99999
      setLoginError(code)
    } finally {
      setLoad(false)
    }
  }

  const login = async () => {
    setLoad(true)
    try {
      provider.addScope('https://www.googleapis.com/auth/userinfo.profile')
      provider.addScope('https://www.googleapis.com/auth/userinfo.email')
      const result = await signInWithPopup(auth, provider)
      const names = result.user?.displayName?.split(' ') || ['', '']
      const firstName = names[0]
      const lastName = names[names.length - 1]
      const { accessToken } = GoogleAuthProvider.credentialFromResult(result) as OAuthCredential
      const utm = utmServices.utmRead()
      const lang = textServices.language
      const body: any = { accessToken, firstName, lastName, utm, lang }

      if (birthday) {
        body.birthday = birthday;
      }

      const loginResponse = await axios.post(
        AUTH, body,
        {
          headers: { Authorization: `Bearer ${(result.user as any).accessToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
        }
      )
      setLoad(false)
      const hashId = loginResponse?.data?.profile?.hashId;

      if (!loginResponse?.data?.profile?.termsOfUse || !loginResponse?.data?.profile?.privacyPolicy) {
        gtag('event', 'sign_up', {
          event_label: 'SignUp - Google',
          method: 'Google'
        })

        dlTrackEvent('sign_up', {
          method: 'google'
        }, hashId)

        MetaCAPIService.emitCompleteRegistration(result.user.email, firstName, lastName, '', window.location.href, 'Meta', MetaCAPIService.uuidv4())
        return setAllowCookies({
          callback: (data: { termsOfUse: boolean; subscribe: boolean; code?: string }) =>
            callback({ ...data, session: result, user: loginResponse?.data?.profile, prize: loginResponse?.data?.prize }),
          isAllow: true,
          hashId
        })
      } else {
        gtag('event', 'login', {
          event_label: 'Login - Google',
          method: 'Google'
        })
        dlTrackEvent('login', {
          method: 'google'
        }, hashId)

        if (flowType === 'alreadyParticipate') return setAllowCookies({
          callback: () => callback({ session: result, user: loginResponse?.data?.profile, prize: loginResponse?.data?.prize }),
          isAllow: false,
          hashId
        })
      }
      callback({ session: result, user: loginResponse?.data?.profile, prize: loginResponse?.data?.prize })
    } catch (error) {
      if ((error as AxiosError).isAxiosError) {
        const code = (error as AxiosError).response?.data.code || 99999
        setLoginError(code)
      } else {
        const typedError = error as AuthError
        setError(typedError)
        const isAccountExistsError = typedError.code === 'auth/account-exists-with-different-credential';
        const errorKey = isAccountExistsError ? `${typedError.code}-google` : typedError.code;
        setFirebaseError(errorKey);
      }
    } finally {
      setLoad(false)
    }
  }

  const handleClick = () => {
    dlTrackEvent('user_interaction', {
      action: "click",
      element: 'google',
      section: "login"
    })
    gtag('event', 'Click', { event_category: GTM_EVENT_CATEGORY, event_label: 'Click - Google', value: 'Google' })

    const inBrowserApp = isInAppBrowser()
    if (inBrowserApp) return setAlertAppView(true);

    if (false as any || ["dosequis", "tecate", "mixx", "cristal", "heineken00", "solMezclas", 'massive'].includes(THEME)) {
      return goToUrlGoogle(null, utmServices.utmRead())
    }

    return login()
  }

  return (
    <>
      <AlertAppView open={alertAppView} setOpen={setAlertAppView} social='Google' />
      {
        isHorizontal
          ? (
            <div onClick={handleClick} className='w-full cursor-pointer bg-white google border border-solid rounded-2xl flex items-center justify-center gap-3' style={{ borderColor: '#747775', padding: '10px 12px' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M17.64 9.20456C17.64 8.56637 17.5827 7.95274 17.4764 7.36365H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8196H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20456Z" fill="#4285F4" />
                <path fillRule="evenodd" clipRule="evenodd" d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8373 3.96409 10.71H0.957272V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853" />
                <path fillRule="evenodd" clipRule="evenodd" d="M3.96409 10.71C3.78409 10.17 3.68182 9.59319 3.68182 9.00001C3.68182 8.40683 3.78409 7.83001 3.96409 7.29001V4.95819H0.957273C0.347727 6.17319 0 7.54774 0 9.00001C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05" />
                <path fillRule="evenodd" clipRule="evenodd" d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957272 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335" />
              </svg>
              <p className='roboto text-sm font-medium' style={{ color: '#1F1F1F' }}>Sign in with Google</p>
            </div>
          ) : (
            <div onClick={handleClick} className="cursor-pointer">
              <GoogleIcon className='google' />
            </div>
          )
      }
    </>
  )
}

export default Google
